/* global ppd */

import fetcher from 'modules/fetcher'
// eslint-disable-next-line no-restricted-imports -- non-React module
import { t } from 'i18n/utils'
import { notify } from 'utils/notify'

export const PHONE_INTEGRATION_KEY = 'pipeline_phone'

/**
 * Check if Pipeline Phone is enabled in Features
 * @returns {boolean}
 */
export const isPipelinePhoneEnabled = () => {
  const phoneSupport = window.Features?.phone_support
  return Array.isArray(phoneSupport) && phoneSupport.some(
    (provider) => provider?.provider_key === PHONE_INTEGRATION_KEY
  )
}

export const createTokenData = async (appKey) => {
  return fetcher(`/api/v3/auth/create_jwt_token.json?app_key=${appKey}`, {
    method: 'POST',
    credentials: 'include'
  })
}

const toggleInstallationFetch = async (installationId, active, body = {}) => {
  const action = active ? 'enable' : 'disable'

  return fetcher(`${ppd.pipelineIntegrationsUrl}/api/v1/installations/${installationId}/${action}.json`, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify(body)
  })
}

/**
 * Fetches the phone integration and installation data
 * Uses show endpoint with integration key for efficient single-call lookup
 * @param {number} userId - The current user's ID
 * @returns {Promise<{integration: object|null, installation: object|null, enabledUserIds: number[], hasProvisionedPhone: boolean, status: string, error: string|null}>}
 */
export const fetchPhoneInstallationData = async (userId) => {
  const result = {
    integration: null,
    installation: null,
    enabledUserIds: [],
    hasProvisionedPhone: false,
    status: 'loading',
    error: null
  }

  try {
    // Fetch installation by integration key - returns full details in one call
    const installationDetails = await fetcher(
      `${ppd.pipelineIntegrationsUrl}/api/v1/installations/${PHONE_INTEGRATION_KEY}.json?user_id=${userId}`,
      {
        method: 'GET',
        credentials: 'include'
      }
    )

    result.installation = installationDetails
    result.integration = installationDetails.integration

    // Extract enabled users and provisioned phone
    const extracted = extractInstallationData({
      integrationSettings: installationDetails.integration?.integration_settings,
      installationSettings: installationDetails.installation_settings,
      userId
    })
    Object.assign(result, extracted)

    result.status = 'success'
    return result
  } catch (err) {
    // 404 means no installation exists yet - fetch integration info for install flow
    if (err.status === 404) {
      try {
        const integrations = await fetcher(`${ppd.pipelineIntegrationsUrl}/api/v1/integrations.json`, {
          method: 'GET',
          credentials: 'include'
        })

        result.integration = integrations.find(int => int.key === PHONE_INTEGRATION_KEY) || null
        result.status = 'success'
        return result
      } catch (intErr) {
        console.error('[fetchPhoneInstallationData] Failed to fetch integrations:', intErr)
      }
    }

    console.error('[fetchPhoneInstallationData] Failed to fetch phone integration data:', err)
    result.status = 'error'
    result.error = err.message || 'Failed to fetch phone integration data'
    return result
  }
}

/**
 * Extract enabled users and provisioned phone from installation data.
 * Shared by both initial fetch and installation update paths.
 * @param {object} params
 * @param {object[]} params.integrationSettings - integration_settings from the integration
 * @param {object[]} params.installationSettings - installation_settings from the installation
 * @param {number} params.userId - current user ID
 * @returns {{enabledUserIds: number[], hasProvisionedPhone: boolean, provisionedPhoneNumber: string|null}}
 */
export const extractInstallationData = ({ integrationSettings, installationSettings, userId }) => {
  const result = { enabledUserIds: [], hasProvisionedPhone: false, provisionedPhoneNumber: null }

  const enableUsersSetting = integrationSettings?.find(s => s.setting_type === 'enable_users')
  if (enableUsersSetting) {
    const instSetting = installationSettings?.find(s => s.integration_setting_id === enableUsersSetting.id)
    if (instSetting?.value?.selections) {
      result.enabledUserIds = instSetting.value.selections
    }
  }

  const twilioPhoneIntSetting = integrationSettings?.find(s => s.key === 'twilio_phone_number')
  if (twilioPhoneIntSetting) {
    const userPhoneSetting = installationSettings?.find(
      s => s.integration_setting_id === twilioPhoneIntSetting.id && s.user_id === userId
    )
    if (userPhoneSetting?.value) {
      result.hasProvisionedPhone = true
      result.provisionedPhoneNumber = userPhoneSetting.value
    }
  }

  return result
}

/**
 * Creates a new phone integration installation
 * @param {object} integration - The phone integration object
 * @returns {Promise<object>} The created installation
 */
export const installPhoneIntegration = async (integration) => {
  if (!integration) {
    throw new Error('No phone integration provided')
  }

  const tokenData = await createTokenData(integration.app_key)
  const installation = await fetcher(`${ppd.pipelineIntegrationsUrl}/api/v1/installations.json`, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      integration_id: integration.id,
      token_data: tokenData
    })
  })

  return installation
}

export const toggleInstallation = async ({ installation, integration, active, onSuccess, onError }) => {
  const integrationName = integration?.name || installation?.integration?.name
  const appKey = integration?.app_key || installation?.integration?.app_key
  const installationId = installation?.id

  window.trackableEvent?.integrations?.toggledActive?.({ integration_name: integrationName, active })

  try {
    let updatedInstallation

    if (active && installation.token_data?.jwt?.token == null) {
      const tokenData = await createTokenData(appKey)
      updatedInstallation = await toggleInstallationFetch(installationId, active, { installation: { token_data: tokenData } })
    } else {
      updatedInstallation = await toggleInstallationFetch(installationId, active)
    }

    if (onSuccess) {
      onSuccess(updatedInstallation)
    }

    return updatedInstallation
  } catch (err) {
    const message = err.json ? Object.entries(err.json.errors).map(([_key, values]) => values.join(', ')) : 'Unknown error'
    notify(t('admin.integrations.toggleFailed', { message }), { type: 'error' })

    if (onError) {
      onError(err)
    }

    throw err
  }
}
